import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { extractErrorMessage } from "@platform/api";
import { getSuiteReportOptions, type SuiteReport } from "@platform/api/golden";
import { Alert, Card, Chip, Link, Typography } from "@platform/design-system";
import { useQuery } from "@tanstack/react-query";
import { Fragment } from "react";
import { Link as RouterLink, useParams } from "react-router";

import { useGoldenClient } from "../api/GoldenClientProvider";
import { usePageTitle } from "../hooks/usePageTitle";
import { percent } from "../shared/values";

import type { ReactNode } from "react";

export const SUITE_PAGE_TEST_ID = "golden-suite";

const SUITE_ID = /^[1-9][0-9]*$/;

// A version the runner never reported, or a local run.
const NOT_RECORDED = "not recorded";

// The comparisons a suite report groups fields by, in the order they are read.
const COMPARISONS: readonly { readonly key: string; readonly title: string }[] =
  [
    {
      key: "regressed",
      title: "Regressed: the reference was right, the candidate is wrong",
    },
    {
      key: "improved",
      title: "Improved: the reference was wrong, the candidate is right",
    },
    { key: "unstable", title: "Unstable: no majority across repetitions" },
    { key: "still_wrong", title: "Still wrong" },
  ];

const CaseLinks = ({ ids }: { readonly ids: readonly number[] }): ReactNode =>
  ids.map((id, index) => (
    <Fragment key={id}>
      {index > 0 && ", "}
      <Link component={RouterLink} to={`/cases/${id}`}>
        #{id}
      </Link>
    </Fragment>
  ));

const Notice = ({
  testId,
  children,
}: {
  readonly testId: string;
  readonly children: ReactNode;
}): ReactNode => (
  <Alert data-testid={`${SUITE_PAGE_TEST_ID}-${testId}`} severity="warning">
    {children}
  </Alert>
);

const FieldTable = ({
  testId,
  title,
  fields,
}: {
  readonly testId: string;
  readonly title: string;
  readonly fields: Readonly<Record<string, readonly number[]>>;
}): ReactNode => (
  <Card data-testid={`${SUITE_PAGE_TEST_ID}-${testId}`} title={title}>
    <Table size="small" aria-label={title}>
      <TableHead>
        <TableRow>
          <TableCell>Field key</TableCell>
          <TableCell>Cases</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Object.entries(fields).map(([fieldKey, ids]) => (
          <TableRow key={fieldKey}>
            <TableCell sx={{ fontFamily: "monospace" }}>{fieldKey}</TableCell>
            <TableCell>
              <CaseLinks ids={ids} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Card>
);

const PairingChanges = ({
  testId,
  title,
  changes,
}: {
  readonly testId: string;
  readonly title: string;
  readonly changes: SuiteReport["pairing_regressions"];
}): ReactNode => (
  <Card data-testid={`${SUITE_PAGE_TEST_ID}-${testId}`} title={title}>
    {Object.entries(changes).map(([collection, entries]) => (
      <Typography key={collection} variant="body-medium">
        <Box component="span" sx={{ fontFamily: "monospace" }}>
          {collection}
        </Box>
        :{" "}
        {entries.map(([caseId, path]) => (
          <Fragment key={`${caseId}${path}`}>
            <Link component={RouterLink} to={`/cases/${caseId}`}>
              #{caseId}
            </Link>{" "}
            <Box component="span" sx={{ fontFamily: "monospace" }}>
              {path}
            </Box>
            ;{" "}
          </Fragment>
        ))}
      </Typography>
    ))}
  </Card>
);

const Totals = ({ report }: { readonly report: SuiteReport }): ReactNode => (
  <Table
    size="small"
    aria-label="Reference and candidate"
    data-testid={`${SUITE_PAGE_TEST_ID}-totals`}
  >
    <TableHead>
      <TableRow>
        <TableCell />
        <TableCell>Reference</TableCell>
        <TableCell>Candidate</TableCell>
        <TableCell>Change</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow>
        <TableCell>Field accuracy</TableCell>
        <TableCell>{percent(report.reference.field_accuracy)}</TableCell>
        <TableCell>{percent(report.candidate.field_accuracy)}</TableCell>
        <TableCell>{percent(report.field_accuracy_change)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell>Case pass rate</TableCell>
        <TableCell>{percent(report.reference.case_pass_rate)}</TableCell>
        <TableCell>{percent(report.candidate.case_pass_rate)}</TableCell>
        <TableCell>{percent(report.case_pass_rate_change)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell>Runs</TableCell>
        <TableCell>{report.reference.runs}</TableCell>
        <TableCell>{report.candidate.runs}</TableCell>
        <TableCell>
          {report.failed_runs > 0 && (
            <Chip
              size="small"
              variant="subtle"
              color="danger"
              leadingVisual={false}
              label={`${report.failed_runs} failed`}
            />
          )}
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
);

/**
 * The RUMI version each case's runs ran, and the one they were compared with. The
 * service records one per case, so a deploy between cases shows here.
 */
const Versions = ({ report }: { readonly report: SuiteReport }): ReactNode => {
  const caseIds = Object.keys(report.candidate_versions)
    .map(Number)
    .sort((a, b) => a - b);
  if (caseIds.length === 0) return null;
  const unreferenced = new Set(report.unreferenced_case_ids);
  const referenceOf = (caseId: number): string =>
    report.reference_versions[String(caseId)] ??
    (unreferenced.has(caseId) ? "nothing earlier" : NOT_RECORDED);

  return (
    <Card
      data-testid={`${SUITE_PAGE_TEST_ID}-versions`}
      title="Versions compared"
    >
      <Table size="small" aria-label="Versions compared">
        <TableHead>
          <TableRow>
            <TableCell>Case</TableCell>
            <TableCell>Reference</TableCell>
            <TableCell>Candidate</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {caseIds.map((caseId) => (
            <TableRow
              key={caseId}
              data-testid={`${SUITE_PAGE_TEST_ID}-version-${caseId}`}
            >
              <TableCell>
                <CaseLinks ids={[caseId]} />
              </TableCell>
              <TableCell>{referenceOf(caseId)}</TableCell>
              <TableCell>
                {report.candidate_versions[String(caseId)] ?? NOT_RECORDED}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

const Report = ({ report }: { readonly report: SuiteReport }): ReactNode => {
  const hasEntries = (record: object): boolean =>
    Object.keys(record).length > 0;

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="header-large" component="h1">
          Suite #{report.suite_id}
        </Typography>
        <Chip
          size="small"
          variant="subtle"
          leadingVisual={false}
          label={report.state}
        />
      </Box>
      <Totals report={report} />
      <Versions report={report} />
      {report.error && (
        <Alert data-testid={`${SUITE_PAGE_TEST_ID}-stopped`} severity="error">
          The suite stopped: {report.error}
        </Alert>
      )}
      {report.unfinished_runs > 0 && (
        <Notice testId="unfinished">
          {report.unfinished_runs} run(s) are still queued or running; the rates
          are not final.
        </Notice>
      )}
      {report.mixed_input_case_ids.length > 0 && (
        <Notice testId="mixed-inputs">
          Left out of the rates: the inputs of these cases changed between
          repetitions (a preprod refresh), so they could not be compared on one
          set of inputs. Run them again:{" "}
          <CaseLinks ids={report.mixed_input_case_ids} />
        </Notice>
      )}
      {report.mixed_version_case_ids.length > 0 && (
        <Notice testId="mixed-versions">
          Left out of the rates: these cases ran on more than one RUMI version
          during the suite (a deploy in the middle of it), so they could not be
          compared as one version. Run them again:{" "}
          <CaseLinks ids={report.mixed_version_case_ids} />
        </Notice>
      )}
      {report.unreferenced_case_ids.length > 0 && (
        <Notice testId="unreferenced">
          Nothing earlier to compare these cases with, so no regression could be
          looked for in them, and the suite cannot pass while it has any:{" "}
          <CaseLinks ids={report.unreferenced_case_ids} />
        </Notice>
      )}
      {report.outdated_pattern_cases.length > 0 && (
        <Notice testId="outdated-patterns">
          These cases have runs from before the current scored-field list, so
          fields added since are not compared for them until they run again:{" "}
          <CaseLinks ids={report.outdated_pattern_cases} />
        </Notice>
      )}
      {report.provisional_case_ids.length > 0 && (
        <Notice testId="provisional">
          Provisional: these cases have discrepancies without a review, so their
          scores can still change:{" "}
          <CaseLinks ids={report.provisional_case_ids} />
        </Notice>
      )}
      {hasEntries(report.pairing_regressions) && (
        <PairingChanges
          testId="pairing-regressions"
          title="Pairing regressions"
          changes={report.pairing_regressions}
        />
      )}
      {hasEntries(report.pairing_improvements) && (
        <PairingChanges
          testId="pairing-improvements"
          title="Pairing improvements"
          changes={report.pairing_improvements}
        />
      )}
      {COMPARISONS.map(({ key, title }) => {
        const fields = report.fields[key] ?? {};
        return hasEntries(fields) ? (
          <FieldTable key={key} testId={key} title={title} fields={fields} />
        ) : null;
      })}
      {hasEntries(report.ambiguous) && (
        <FieldTable
          testId="ambiguous"
          title="Ambiguous pairings (not in either rate)"
          fields={report.ambiguous}
        />
      )}
    </>
  );
};

const LoadedReport = ({ suiteId }: { readonly suiteId: number }): ReactNode => {
  const client = useGoldenClient();
  const report = useQuery(
    getSuiteReportOptions({ client, path: { suite_id: suiteId } }),
  );

  if (report.isSuccess) return <Report report={report.data} />;
  return report.isError ? (
    <Alert data-testid={`${SUITE_PAGE_TEST_ID}-error`} severity="error">
      {extractErrorMessage(report.error, "Failed to load the report")}
    </Alert>
  ) : (
    <CircularProgress aria-label="Loading the report" />
  );
};

/** A suite's comparison of a candidate RUMI version with the reference, case by case. */
export const SuitePage = (): ReactNode => {
  const { suiteId = "" } = useParams();
  usePageTitle(`Suite ${suiteId}`);

  return (
    <Box
      data-testid={SUITE_PAGE_TEST_ID}
      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
    >
      {SUITE_ID.test(suiteId) ? (
        <LoadedReport key={suiteId} suiteId={Number(suiteId)} />
      ) : (
        <Alert
          data-testid={`${SUITE_PAGE_TEST_ID}-not-found`}
          severity="warning"
        >
          There is no suite {suiteId}.
        </Alert>
      )}
    </Box>
  );
};
